import { CheckOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import { useState } from "react";
import AuditHistory from "./AuditHistory";
import { loginAsDistributor } from "./loginAs";
import {
  Distributor,
  DistributorTopup,
  useCreateDistributorMutation,
  useDisableDistributorMutation,
  useGetDistributorTopupsQuery,
  useGetDistributorsQuery,
  useReviewDistributorTopupMutation,
} from "../../api/admin/distributors/distributorsApi";

function formatMoney(cents?: number, currency = "SGD") {
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency,
    currencyDisplay: "code",
  }).format((cents || 0) / 100);
}

function statusColor(status?: string) {
  switch ((status || "").toUpperCase()) {
    case "ACTIVE":
    case "APPROVED":
      return "green";
    case "PENDING_REVIEW":
    case "PENDING_PAYMENT":
      return "gold";
    case "REJECTED":
    case "INACTIVE":
    case "DISABLED":
      return "red";
    default:
      return "default";
  }
}

function dollarsToCents(value?: number) {
  return Math.round(Number(value || 0) * 100);
}

export default function DistributorsPage() {
  const [tab, setTab] = useState("distributors");
  const [auditDistributorId, setAuditDistributorId] = useState<string>();
  const [openingDistributorId, setOpeningDistributorId] = useState<string>();
  const [isCreating, setIsCreating] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { data: distributors = [], isLoading: loadingDistributors, error: distributorError } = useGetDistributorsQuery();
  const { data: pendingTopups = [], isLoading: loadingTopups, error: topupError } = useGetDistributorTopupsQuery("PENDING_REVIEW");
  const [createDistributor, createState] = useCreateDistributorMutation();
  const [disableDistributor] = useDisableDistributorMutation();
  const [reviewTopup, reviewState] = useReviewDistributorTopupMutation();

  const openDistributor = async (distributorId: string) => {
    setOpeningDistributorId(distributorId);
    try {
      await loginAsDistributor(distributorId);
    } catch (error) {
      messageApi.error(
        error instanceof Error ? error.message : "Access failed",
      );
    } finally {
      setOpeningDistributorId(undefined);
    }
  };

  const onCreate = async (values: any) => {
    await createDistributor({
      code: values.code,
      name: values.name,
      ownerEmail: values.ownerEmail,
      ownerName: values.ownerName,
      temporaryPassword: values.temporaryPassword,
      initialBalanceCents: dollarsToCents(values.initialBalanceDollars),
    }).unwrap();
    form.resetFields();
    setIsCreating(false);
    messageApi.success("Distributor created");
  };

  const onDisable = (distributor: Distributor) => {
    Modal.confirm({
      title: "Disable distributor",
      content: `Disable ${distributor.name}?`,
      onOk: async () => {
        await disableDistributor(distributor.id).unwrap();
        messageApi.success("Distributor disabled");
      },
    });
  };

  const onReviewTopup = (topup: DistributorTopup, action: "approve" | "reject") => {
    Modal.confirm({
      title: action === "approve" ? "Approve top-up" : "Reject top-up",
      content: `${action === "approve" ? "Approve" : "Reject"} ${formatMoney(topup.amountCents, topup.currency)} for ${topup.distributorName || topup.distributorCode}?`,
      onOk: async () => {
        await reviewTopup({
          distributorId: topup.distributorId,
          topUpId: topup.topUpId,
          action,
        }).unwrap();
        messageApi.success(action === "approve" ? "Top-up approved" : "Top-up rejected");
      },
    });
  };

  return (
    <>
      {contextHolder}
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Space align="start" style={{ justifyContent: "space-between", width: "100%" }}>
          <div>
            <Typography.Title level={3} style={{ marginBottom: 4 }}>Distributor Portal</Typography.Title>
            <Typography.Text type="secondary">Onboard distributors and review wallet top-ups.</Typography.Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreating(true)}>
            Add distributor
          </Button>
        </Space>
        {(distributorError || topupError) && (
          <Alert type="error" showIcon message="Unable to load distributor portal data" />
        )}
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: "distributors",
              label: "Distributors",
              children: (
                <Card>
                  <Table<Distributor>
                    rowKey="id"
                    loading={loadingDistributors}
                    dataSource={distributors}
                    columns={[
                      { title: "Code", dataIndex: "code" },
                      { title: "Name", dataIndex: "name" },
                      {
                        title: "Status",
                        dataIndex: "status",
                        render: (status) => <Tag color={statusColor(status)}>{status}</Tag>,
                      },
                      {
                        title: "Wallet",
                        dataIndex: "walletBalanceCents",
                        align: "right",
                        render: (value) => formatMoney(value),
                      },
                      { title: "Owner", dataIndex: "ownerEmail" },
                      {
                        title: "Actions",
                        render: (_, distributor) => (
                          <Space wrap>
                            <Button
                              disabled={
                                distributor.status !== "ACTIVE"
                                || Boolean(openingDistributorId)
                              }
                              loading={
                                openingDistributorId === distributor.id
                              }
                              onClick={() => openDistributor(distributor.id)}
                            >
                              Log in as distributor
                            </Button>
                            <Button
                              onClick={() => {
                                setAuditDistributorId(distributor.id);
                                setTab("audit");
                              }}
                            >
                              View audit
                            </Button>
                            <Button
                              danger
                              disabled={distributor.status === "INACTIVE"}
                              onClick={() => onDisable(distributor)}
                            >
                              Disable
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </Card>
              ),
            },
            {
              key: "audit",
              label: "Audit History",
              children: (
                <AuditHistory
                  key={auditDistributorId || "audit"}
                  distributors={distributors}
                  distributorId={auditDistributorId}
                  onDistributorChange={setAuditDistributorId}
                />
              ),
            },
            {
              key: "topups",
              label: "Top-up approvals",
              children: (
                <Card>
                  <Table<DistributorTopup>
                    rowKey="topUpId"
                    loading={loadingTopups || reviewState.isLoading}
                    dataSource={pendingTopups}
                    columns={[
                      { title: "Distributor", render: (_, row) => row.distributorName || row.distributorCode || row.distributorId },
                      { title: "Top-up", dataIndex: "topUpId" },
                      {
                        title: "Amount",
                        dataIndex: "amountCents",
                        align: "right",
                        render: (value, row) => formatMoney(value, row.currency),
                      },
                      { title: "Bank reference", dataIndex: "bankReference" },
                      { title: "Requested by", dataIndex: "requestedBy" },
                      { title: "Created", dataIndex: "createdAt" },
                      {
                        title: "Actions",
                        render: (_, topup) => (
                          <Space>
                            <Button icon={<CheckOutlined />} onClick={() => onReviewTopup(topup, "approve")}>
                              Approve
                            </Button>
                            <Button danger icon={<CloseOutlined />} onClick={() => onReviewTopup(topup, "reject")}>
                              Reject
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </Card>
              ),
            },
          ]}
        />
      </Space>
      <Modal
        title="Add distributor"
        open={isCreating}
        onCancel={() => setIsCreating(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onCreate}>
          <Form.Item name="code" label="Distributor code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Distributor name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="ownerEmail" label="Owner email" rules={[{ required: true, type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="ownerName" label="Owner name">
            <Input />
          </Form.Item>
          <Form.Item name="temporaryPassword" label="Temporary password">
            <Input.Password />
          </Form.Item>
          <Form.Item name="initialBalanceDollars" label="Initial wallet credit">
            <InputNumber min={0} precision={2} style={{ width: "100%" }} prefix="$" />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={createState.isLoading}>
              Create
            </Button>
            <Button onClick={() => setIsCreating(false)}>Cancel</Button>
          </Space>
        </Form>
      </Modal>
    </>
  );
}
